import { useState } from 'react'

import { PlayIcon } from '@radix-ui/react-icons'
import dynamic from 'next/dynamic'
import Image from 'next/image'

import Box from 'components/atomic/atoms/box'
import Button from 'components/atomic/atoms/button'
import Logo from 'components/atomic/atoms/logo'

const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false })

type Props = {
  url: string
}

const getYouTubeId = (url: string) => {
  const match = url.match(
    /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com|youtu\.be)\/(?:watch\?v=)?(.+)$/,
  )
  return match?.[1] ?? ''
}

const YoutubeWidget = ({ url }: Props) => {
  const [init, setInit] = useState(false)
  const [playing, setPlaying] = useState(false)
  const [finished, setFinished] = useState(false)
  const videoId = getYouTubeId(url)

  const onPlay = () => {
    setPlaying(true)
  }

  const onPause = () => {
    setPlaying(false)
  }

  const onEnded = () => {
    setPlaying(false)
    setFinished(true)
  }

  const replay = () => {
    setFinished(false)
    setPlaying(true)
  }

  return (
    <Box
      data-widget="youtube"
      css={{ aspectRatio: '16/9', borderRadius: '$md', overflow: 'hidden' }}
    >
      {finished ? (
        <Box
          flex="column"
          items="center"
          justify="center"
          css={{ size: '$full', bgc: '$bg3', spaceY: 4 }}
        >
          <Logo.WithText size="lg" />
          <Button variant="secondary" icon={PlayIcon} onClick={replay}>
            Watch again?
          </Button>
        </Box>
      ) : init ? (
        <ReactPlayer
          url={url}
          playing={playing}
          height="100%"
          width="100%"
          onEnded={onEnded}
          onPlay={onPlay}
          onPause={onPause}
          controls
        />
      ) : (
        <Box
          relative
          flex="column"
          items="center"
          justify="center"
          css={{ size: '$full', bgc: '$bg3' }}
          onClick={() => {
            setInit(true)
            setPlaying(true)
          }}
        >
          <Button variant="secondary" icon={PlayIcon} css={{ zIndex: 1 }}>
            Watch
          </Button>
          <Image
            src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
            alt="Youtube video thumbnail"
            fill
            style={{ objectFit: 'cover' }}
          />
        </Box>
      )}
    </Box>
  )
}

export default YoutubeWidget
